// web-app/src/utils/runEndFillReach.js
//
// Where along its wall a run can actually STAND, for the run-end drag.
//
// `runEndRange` let an end go as far as the wall's end. But a door, or a
// window whose sill is below the counter, is a stretch the fill puts no
// cabinet in (`@shared-lib/cabinet-runs/openings`), so an end dragged across
// one drew its handle out in the doorway while the cabinets stopped at the
// opening's edge — and the release saved that end, with nothing standing
// between it and the last cabinet.
//
// So the question is asked of the fill itself rather than of the openings:
// solve the run once with this end pushed as far as it may go, and read off
// which stretches of the wall its pieces cover. A rule about openings would
// be a second copy of the fill's own keep-out rules (sill height, buffers,
// the run's own counter line, a scan's `_baseCabinetExempt` window), and
// would still miss a leftover too short for any cabinet.
//
// What the drag does with the answer (`runEndRange`): the end may grow only
// to the far edge of the last covered stretch, and an end released inside an
// uncovered stretch goes back to the near edge — where the cabinets before it
// stop. A stretch beyond a doorway stays reachable: the end crosses the door
// to it.

import { getWallAxes } from '@shared-lib/kitchen-assembly/wallMath';
import { objectFootprint } from '@shared-lib/geometry/objectFootprint';
import { applianceBays } from '@shared-lib/cabinet-runs/bays';
import { runDepthSpec } from '@shared-lib/cabinet-runs/profiles';
import { materializeRunObjects } from './cabinetRunGeometry';

const IN = 0.0254;
// Two pieces closer than this along the wall are one stretch: a filler's
// rounding or a reveal is not a gap anything could stand in.
const MERGE_GAP_M = 1 * IN;
// How far in front of the wall face a piece may reach and still be this
// segment's — a counter's overhang, a door on its hinge.
const FRONT_SLACK_M = 6 * IN;

/** A piece's span along the wall, or null when it is not on this segment. */
function spanOnWall(object, axes, depth) {
  const corners = objectFootprint(object);
  if (!corners) return null;
  let aLo = Infinity;
  let aHi = -Infinity;
  let nLo = Infinity;
  let nHi = -Infinity;
  for (const c of corners) {
    const dx = c.x - axes.start.x;
    const dz = c.z - axes.start.z;
    const a = dx * axes.wallDir.x + dz * axes.wallDir.z;
    const n = dx * axes.wallNormal.x + dz * axes.wallNormal.z;
    aLo = Math.min(aLo, a);
    aHi = Math.max(aHi, a);
    nLo = Math.min(nLo, n);
    nHi = Math.max(nHi, n);
  }
  // Against this wall: within the run's depth of the face. A perpendicular
  // leg's pieces stand further out, except the one at the corner, which
  // covers the corner either way.
  if (nHi < -FRONT_SLACK_M || nLo > depth + FRONT_SLACK_M) return null;
  return [aLo, aHi];
}

/** Sorted, merged spans. */
function mergeSpans(spans) {
  const sorted = spans.slice().sort((p, q) => p[0] - q[0]);
  const out = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last && s[0] <= last[1] + MERGE_GAP_M)
      last[1] = Math.max(last[1], s[1]);
    else out.push([s[0], s[1]]);
  }
  return out;
}

/**
 * The stretches of the segment's wall the pushed run's pieces cover, sorted
 * and merged, as `[lo, hi]` wall params. Null when the fill could not be asked
 * (no axes, or it placed nothing on this wall).
 */
function coverOf({ room, run, frame, segIndex, push, openings }) {
  const axes = getWallAxes(frame.wall);
  if (!axes) return null;
  const pushed = push();
  if (!pushed) return null;
  const resolve = materializeRunObjects(pushed, room, openings);
  const depth = runDepthSpec(run);
  const spans = [];
  for (const o of resolve?.objects || []) {
    const span = spanOnWall(o, axes, depth);
    if (span) spans.push(span);
  }
  // The run's own appliances stand in their bays, not as run objects.
  for (const b of applianceBays(pushed, segIndex)) {
    if (b.unplaced || !Number.isFinite(b.anchorParam)) continue;
    spans.push([b.anchorParam - b.width / 2, b.anchorParam + b.width / 2]);
  }
  return spans.length ? mergeSpans(spans) : null;
}

// Per room and stored run, because the handles ask on every frame the run is
// selected and a drag asks on every pointer move — always about the same
// room, the same run and the same pushed end. Openings arrive as a fresh
// array per call, so they are keyed by what the fill reads of them.
const cache = new WeakMap();

function openingsKey(openings) {
  return (openings || [])
    .map(
      (o) =>
        `${o.id || o.structureOpeningId}:${o.center?.x},${o.center?.y},${o.center?.z}:${o.dimensions?.x},${o.dimensions?.y}`
    )
    .join('|');
}

/**
 * The covered stretches of a WALL-BACKED end's segment with the end pushed to
 * `extendTo`. Null for a free-standing leg (no wall, so nothing on it a
 * cabinet must skip) and whenever the fill gives no answer.
 *
 * @param {Object} a
 * @param {Object} a.room
 * @param {Object} a.stored   the stored run (the memo key)
 * @param {Object} a.run      the rehydrated run
 * @param {Object} a.seg      the end's rehydrated segment
 * @param {number} a.segIndex its index in the rehydrated run
 * @param {Object} a.frame    `segmentFrame(seg)`
 * @param {'start'|'end'} a.end
 * @param {number} a.extendTo the end's param pushed as far as it may go
 * @param {Function} a.push   () => the rehydrated run with the end there
 * @param {Array}  a.openings `getRunOpenings(...)`
 * @returns {Array<[number, number]>|null}
 */
export function runEndFillCover(a) {
  const { room, stored, seg, end, extendTo, openings } = a;
  if (!room || !stored || !seg || seg.freestanding) return null;
  if (!Number.isFinite(extendTo) || !Array.isArray(openings)) return null;
  let byRun = cache.get(room);
  if (!byRun) {
    byRun = new WeakMap();
    cache.set(room, byRun);
  }
  let byKey = byRun.get(stored);
  if (!byKey) {
    byKey = new Map();
    byRun.set(stored, byKey);
  }
  const key = `${end}|${extendTo.toFixed(5)}|${openingsKey(openings)}`;
  if (!byKey.has(key)) byKey.set(key, coverOf(a));
  return byKey.get(key);
}
